import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTrade } from '@/lib/trade';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  PROCESSES_KEY,
  UNIT_KINDS,
  processesApi,
  type CarpentrySettings,
  type ContractRemindersSettings,
  type SupplierInvoiceRemindersSettings,
  type GreenInvoiceBillingSettings,
  type JobLifecycleSettings,
  type Process,
  type QuoteRemindersSettings,
} from './processes-api';


export function ProcessSettingsDialog({
  process,
  onClose,
}: {
  process: Process;
  onClose: () => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({
    ...(process.settings ?? {}),
  }));

  const save = useMutation({
    mutationFn: () => processesApi.update(process.key, { settings: draft }),
    onSuccess: () => {
      toast.success(t('settings.saved'));
      void qc.invalidateQueries({ queryKey: PROCESSES_KEY });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(t('settings.saveFailed'), err instanceof ApiError ? err.message : undefined),
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent aria-describedby="process-settings-desc">
        <DialogHeader>
          <DialogTitle>{t('settings.title', { name: process.name })}</DialogTitle>
          <DialogDescription id="process-settings-desc">{process.tagline}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {process.key === 'job-lifecycle' ? (
            <JobLifecycleForm value={draft as JobLifecycleSettings} onChange={setDraft} />
          ) : process.key === 'quote-reminders' ? (
            <QuoteRemindersForm value={draft as QuoteRemindersSettings} onChange={setDraft} />
          ) : process.key === 'green-invoice-billing' ? (
            <GreenInvoiceBillingForm
              value={draft as GreenInvoiceBillingSettings}
              onChange={setDraft}
            />
          ) : process.key === 'carpentry-drawing-quote' ? (
            <CarpentryForm value={draft as CarpentrySettings} onChange={setDraft} />
          ) : process.key === 'contract-reminders' ? (
            <ContractRemindersForm value={draft as ContractRemindersSettings} onChange={setDraft} />
          ) : process.key === 'supplier-invoice-reminders' ? (
            <BillRemindersForm value={draft as SupplierInvoiceRemindersSettings} onChange={setDraft} />
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SOURCES: Array<JobLifecycleSettings['sources'][number]> = ['EMAIL', 'CUSTOMER_LINK', 'QUOTE', 'MANUAL'];

function JobLifecycleForm({
  value,
  onChange,
}: {
  value: JobLifecycleSettings;
  onChange: (v: JobLifecycleSettings) => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  const trade = useTrade();
  const toggle = (
    key: 'autoAssign' | 'notifyOnCreate' | 'notifyOnSchedule' | 'notifyOnClose' | 'monthlyBilling',
    label: string,
    hint: string,
  ) => (
    <label className="flex items-start justify-between gap-4 rounded-(--radius-md) border border-border p-3">
      <span>
        <span className="block text-sm font-medium text-fg">{label}</span>
        <span className="block text-xs text-fg-muted">{hint}</span>
      </span>
      <Switch
        checked={value[key]}
        onCheckedChange={(v) => onChange({ ...value, [key]: v })}
        aria-label={label}
      />
    </label>
  );

  return (
    <>
      <fieldset className="space-y-2">
        <legend className="mb-1 text-xs font-medium text-fg-muted">{t('settings.jobLifecycle.sourcesLegend')}</legend>
        {SOURCES.map((s) => (
          <label key={s} className="flex items-center gap-2 text-sm text-fg">
            <Checkbox
              checked={value.sources.includes(s)}
              onCheckedChange={(checked) =>
                onChange({
                  ...value,
                  sources: checked
                    ? [...value.sources, s]
                    : value.sources.filter((x) => x !== s),
                })
              }
            />
            {t(`settings.jobLifecycle.sources.${s}`)}
          </label>
        ))}
      </fieldset>
      <SpecificSources
        value={value.specificSources}
        onChange={(specificSources) => onChange({ ...value, specificSources })}
      />
      {toggle('autoAssign', t('settings.jobLifecycle.autoAssign'), t('settings.jobLifecycle.autoAssignHint', { worker: trade.worker.one }))}
      {toggle('notifyOnCreate', t('settings.jobLifecycle.notifyOnCreate'), t('settings.jobLifecycle.notifyOnCreateHint'))}
      {toggle('notifyOnSchedule', t('settings.jobLifecycle.notifyOnSchedule'), t('settings.jobLifecycle.notifyOnScheduleHint'))}
      {toggle('notifyOnClose', t('settings.jobLifecycle.notifyOnClose'), t('settings.jobLifecycle.notifyOnCloseHint'))}
      {toggle('monthlyBilling', t('settings.jobLifecycle.monthlyBilling'), t('settings.jobLifecycle.monthlyBillingHint'))}
    </>
  );
}

type SpecificSource = JobLifecycleSettings['specificSources'][number];

/**
 * "רק פניות ממנהל הנכס / מהטלפון הזה". ריק = כל פנייה מהערוצים שסומנו.
 * הערך מאומת בשרת (מייל תקין, טלפון ישראלי); כאן רק עוזרים לא לטעות.
 */
function SpecificSources({
  value,
  onChange,
}: {
  value: SpecificSource[];
  onChange: (v: SpecificSource[]) => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  const update = (i: number, patch: Partial<SpecificSource>) =>
    onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <fieldset className="space-y-2 rounded-(--radius-md) border border-border p-3">
      <legend className="px-1 text-xs font-medium text-fg-muted">{t('settings.specific.legend')}</legend>
      <p className="text-2xs text-fg-subtle">
        {value.length === 0
          ? t('settings.specific.allSources')
          : t('settings.specific.onlyThese')}
      </p>
      {value.map((source, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <Select
            value={source.kind}
            onValueChange={(kind) => update(i, { kind: kind as SpecificSource['kind'], value: '' })}
          >
            <SelectTrigger className="w-28" aria-label={t('settings.specific.kindAria')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EMAIL">{t('settings.specific.email')}</SelectItem>
              <SelectItem value="PHONE">{t('settings.specific.phone')}</SelectItem>
            </SelectContent>
          </Select>
          <Input
            className="min-w-40 flex-1"
            type={source.kind === 'EMAIL' ? 'email' : 'tel'}
            inputMode={source.kind === 'EMAIL' ? 'email' : 'tel'}
            placeholder={source.kind === 'EMAIL' ? 'manager@building.co.il' : '050-1234567'}
            aria-label={source.kind === 'EMAIL' ? t('settings.specific.emailAria') : t('settings.specific.phoneAria')}
            value={source.value}
            onChange={(e) => update(i, { value: e.target.value })}
          />
          <Input
            className="w-32"
            placeholder={t('settings.specific.namePlaceholder')}
            aria-label={t('settings.specific.nameAria')}
            value={source.label ?? ''}
            onChange={(e) => update(i, { label: e.target.value || undefined })}
          />
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('settings.specific.removeAria')}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <X aria-hidden />
          </Button>
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onChange([...value, { kind: 'EMAIL', value: '' }])}
      >
        <Plus aria-hidden />
        {t('settings.specific.add')}
      </Button>
    </fieldset>
  );
}

function BillRemindersForm({
  value,
  onChange,
}: {
  value: SupplierInvoiceRemindersSettings;
  onChange: (v: SupplierInvoiceRemindersSettings) => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  const num = (raw: string, min: number, max: number, fallback: number) => Math.min(max, Math.max(min, Math.round(Number(raw) || fallback)));
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={t('settings.bills.leadDays')} htmlFor="br-lead" hint={t('settings.bills.leadDaysHint')}>
        <Input id="br-lead" type="number" dir="ltr" min={0} max={30} value={value.leadDays} onChange={(e) => onChange({ ...value, leadDays: num(e.target.value, 0, 30, 3) })} />
      </Field>
      <Field label={t('settings.bills.approveAfter')} htmlFor="br-approve" hint={t('settings.bills.approveAfterHint')}>
        <Input id="br-approve" type="number" dir="ltr" min={1} max={60} value={value.approveAfterDays} onChange={(e) => onChange({ ...value, approveAfterDays: num(e.target.value, 1, 60, 5) })} />
      </Field>
    </div>
  );
}

function ContractRemindersForm({
  value,
  onChange,
}: {
  value: ContractRemindersSettings;
  onChange: (v: ContractRemindersSettings) => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  const num = (raw: string, min: number, max: number, fallback: number) => Math.min(max, Math.max(min, Math.round(Number(raw) || fallback)));
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={t('settings.contracts.leadDays')} htmlFor="cr-lead" hint={t('settings.contracts.leadDaysHint')}>
        <Input id="cr-lead" type="number" dir="ltr" min={1} max={90} value={value.leadDays} onChange={(e) => onChange({ ...value, leadDays: num(e.target.value, 1, 90, 14) })} />
      </Field>
      <Field label={t('settings.contracts.warrantyDays')} htmlFor="cr-warranty" hint={t('settings.contracts.warrantyDaysHint')}>
        <Input id="cr-warranty" type="number" dir="ltr" min={1} max={180} value={value.warrantyDays} onChange={(e) => onChange({ ...value, warrantyDays: num(e.target.value, 1, 180, 30) })} />
      </Field>
    </div>
  );
}

function QuoteRemindersForm({
  value,
  onChange,
}: {
  value: QuoteRemindersSettings;
  onChange: (v: QuoteRemindersSettings) => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  const [days, setDays] = useState(value.scheduleDays.join(', '));
  return (
    <>
      <Field
        label={t('settings.quoteReminders.days')}
        htmlFor="schedule-days"
        hint={t('settings.quoteReminders.daysHint')}
      >
        <Input
          id="schedule-days"
          dir="ltr"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          onBlur={() => {
            const parsed = days
              .split(/[,\s]+/)
              .map(Number)
              .filter((n) => Number.isInteger(n) && n > 0);
            onChange({ ...value, scheduleDays: parsed });
            setDays(parsed.join(', '));
          }}
        />
      </Field>
      <Field
        label={t('settings.quoteReminders.message')}
        htmlFor="reminder-message"
        hint={t('settings.quoteReminders.messageHint')}
      >
        <Textarea
          id="reminder-message"
          rows={4}
          value={value.message}
          onChange={(e) => onChange({ ...value, message: e.target.value })}
        />
      </Field>
    </>
  );
}

function GreenInvoiceBillingForm({
  value,
  onChange,
}: {
  value: GreenInvoiceBillingSettings;
  onChange: (v: GreenInvoiceBillingSettings) => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  return (
    <>
      <Field label={t('settings.greenInvoice.documentType')} htmlFor="gi-doc-type">
        <Select
          value={String(value.documentType)}
          onValueChange={(v) => onChange({ ...value, documentType: Number(v) as 305 | 320 })}
        >
          <SelectTrigger id="gi-doc-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="305">{t('settings.greenInvoice.taxInvoice')}</SelectItem>
            <SelectItem value="320">{t('settings.greenInvoice.taxInvoiceReceipt')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field
        label={t('settings.greenInvoice.mode')}
        htmlFor="gi-mode"
        hint={t('settings.greenInvoice.modeHint')}
      >
        <Select
          value={value.defaultMode}
          onValueChange={(v) => onChange({ ...value, defaultMode: v as 'MONTHLY' | 'PER_JOB' })}
        >
          <SelectTrigger id="gi-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="MONTHLY">{t('settings.greenInvoice.modeMonthly')}</SelectItem>
            <SelectItem value="PER_JOB">{t('settings.greenInvoice.modePerJob')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label={t('settings.greenInvoice.vat')} htmlFor="gi-vat">
        <Select
          value={value.vatMode}
          onValueChange={(v) => onChange({ ...value, vatMode: v as GreenInvoiceBillingSettings['vatMode'] })}
        >
          <SelectTrigger id="gi-vat">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="added">{t('settings.greenInvoice.vatAdded')}</SelectItem>
            <SelectItem value="included">{t('settings.greenInvoice.vatIncluded')}</SelectItem>
            <SelectItem value="exempt">{t('settings.greenInvoice.vatExempt')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field
        label={t('settings.greenInvoice.ceiling')}
        htmlFor="gi-ceiling"
        hint={t('settings.greenInvoice.ceilingHint')}
      >
        <Input
          id="gi-ceiling"
          type="number"
          min={0}
          step={100}
          value={value.autoIssueMax}
          onChange={(e) => onChange({ ...value, autoIssueMax: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
        />
      </Field>
      <label className="flex items-start justify-between gap-4 rounded-(--radius-md) border border-border p-3">
        <span>
          <span className="block text-sm font-medium text-fg">{t('settings.greenInvoice.emailClient')}</span>
          <span className="block text-xs text-fg-muted">
            {t('settings.greenInvoice.emailClientHint')}
          </span>
        </span>
        <Switch
          checked={value.emailClient}
          onCheckedChange={(emailClient) => onChange({ ...value, emailClient })}
          aria-label={t('settings.greenInvoice.emailClientAria')}
        />
      </label>
    </>
  );
}

function CarpentryForm({
  value,
  onChange,
}: {
  value: CarpentrySettings;
  onChange: (v: CarpentrySettings) => void;
}) {
  const { t } = useTranslation(['processes', 'common']);
  const num = (raw: string, fallback: number) => {
    const n = Number(raw);
    return Number.isFinite(n) && raw.trim() !== '' ? n : fallback;
  };
  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t('settings.carpentry.sheetWidth')} htmlFor="sheet-w">
          <Input
            id="sheet-w"
            type="number"
            dir="ltr"
            value={value.sheet.widthCm}
            onChange={(e) =>
              onChange({ ...value, sheet: { ...value.sheet, widthCm: num(e.target.value, value.sheet.widthCm) } })
            }
          />
        </Field>
        <Field label={t('settings.carpentry.sheetHeight')} htmlFor="sheet-h">
          <Input
            id="sheet-h"
            type="number"
            dir="ltr"
            value={value.sheet.heightCm}
            onChange={(e) =>
              onChange({ ...value, sheet: { ...value.sheet, heightCm: num(e.target.value, value.sheet.heightCm) } })
            }
          />
        </Field>
        <Field label={t('settings.carpentry.waste')} htmlFor="waste">
          <Input
            id="waste"
            type="number"
            dir="ltr"
            value={value.wastePct}
            onChange={(e) => onChange({ ...value, wastePct: num(e.target.value, value.wastePct) })}
          />
        </Field>
      </div>
      <fieldset className="space-y-2 rounded-(--radius-md) border border-border p-3">
        <legend className="px-1 text-xs font-medium text-fg-muted">{t('settings.carpentry.laborLegend')}</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {UNIT_KINDS.map((k) => (
            <Field key={k} label={t(`unitKinds.${k}`)} htmlFor={`labor-${k}`}>
              <Input
                id={`labor-${k}`}
                type="number"
                dir="ltr"
                step="0.5"
                value={value.laborHours[k] ?? 0}
                onChange={(e) =>
                  onChange({
                    ...value,
                    laborHours: { ...value.laborHours, [k]: num(e.target.value, value.laborHours[k] ?? 0) },
                  })
                }
              />
            </Field>
          ))}
          <Field label={t('settings.carpentry.perDrawer')} htmlFor="labor-drawer">
            <Input
              id="labor-drawer"
              type="number"
              dir="ltr"
              step="0.5"
              value={value.laborHoursPerDrawer}
              onChange={(e) =>
                onChange({ ...value, laborHoursPerDrawer: num(e.target.value, value.laborHoursPerDrawer) })
              }
            />
          </Field>
        </div>
      </fieldset>
      <p className="text-2xs text-fg-subtle">
        {t('settings.carpentry.note')}
      </p>
    </>
  );
}
